import { useMutation } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { useMemo } from "react";
import { FormProvider, SubmitHandler, useForm, Controller } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import FormFieldViewText from "components/FormFields/ViewText";
import Select from "ds/components/Select";
import { BaseSelectItem } from "ds/components/Select/types";
import useTypedContext from "hooks/useTypedContext";
import { getDrawerFormFix } from "utils/css";

import { SEARCH_TEMPLATE_DEPLOYMENTS } from "../gql";
import { UPDATE_DEPLOYMENT } from "./gql";
import { UpdateDrawerProps, UpdateFormFields } from "./types";
import { useTemplateVersions } from "./useTemplateVersions";

const UpdateDrawer = createDrawer(({ deployment, templateId, refetchQueries }: UpdateDrawerProps) => {
  const drawer = useModal();
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const builderForm = useForm<UpdateFormFields>({
    defaultValues: {
      versionId: deployment.blueprintVersion.id,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty },
    control,
  } = builderForm;

  const { data: versionsData, loading: versionsLoading } = useTemplateVersions(
    templateId,
    deployment.blueprintVersion.id
  );

  const versionItems: BaseSelectItem[] = useMemo(() => {
    const versions = versionsData?.blueprintVersionedGroup?.compatibleVersions || [];

    return versions.map((version) => ({
      value: version.id,
      label: version.version || "Unknown",
    }));
  }, [versionsData]);

  const [updateDeployment, { loading: updateLoading }] = useMutation(UPDATE_DEPLOYMENT, {
    refetchQueries: refetchQueries || [SEARCH_TEMPLATE_DEPLOYMENTS],
  });

  const onSubmit: SubmitHandler<UpdateFormFields> = (formData) => {
    updateDeployment({
      variables: {
        blueprintID: templateId,
        id: deployment.id,
        input: {
          versionId: formData.versionId,
          description: null,
          name: null,
          inputs: null,
        },
      },
    })
      .then(({ data }) => {
        if (data?.blueprintDeploymentUpdate) {
          const newVersion = data.blueprintDeploymentUpdate.blueprintVersion.version;
          reportSuccess({
            message: `Deployment "${deployment.name}" successfully updated to version ${newVersion}`,
          });
          drawer.hide();
        }
      })
      .catch(onError);
  };

  const isSubmitDisabled = updateLoading || versionsLoading || !isDirty;

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Update deployment version" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody fullHeight noPadding>
            <DrawerSection>
              <FormFieldViewText label="Name" value={deployment.name} />
              <FormFieldViewText
                label="Current version"
                value={deployment.blueprintVersion.version}
              />

              <Controller
                name="versionId"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    label="New version"
                    error={fieldState.error?.message}
                    helperText={
                      !versionsLoading && versionItems.length === 0
                        ? "No published versions available for update"
                        : undefined
                    }
                    tooltipInfo="Select the template version to upgrade this deployment to. Only published versions are available."
                  >
                    {({ ariaInputProps }) => (
                      <Select
                        value={field.value}
                        items={versionItems}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        placeholder="Select version"
                        isLoading={versionsLoading}
                        isDisabled={versionsLoading || versionItems.length === 0}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}
              />
            </DrawerSection>
          </DrawerBody>

          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button
                variant="primary"
                type="submit"
                loading={updateLoading}
                disabled={isSubmitDisabled}
              >
                Update
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showUpdateDrawer = createDrawerTrigger(UpdateDrawer);
